import { useMemo } from 'react'
import { Controller, useForm, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Button, Field, FieldError, FieldLabel, Input } from '@clouway/ui'

import { UnsavedChangesGuard } from '@/components/UnsavedChangesGuard'
import type { NetworkInput } from '../types'

// name + apn are required (the wire validate tags); user + pass are optional.
const buildSchema = (requiredMessage: string) =>
  z.object({
    name: z.string().min(1, requiredMessage),
    apn: z.string().min(1, requiredMessage),
    user: z.string(),
    pass: z.string(),
  })

export type NetworkFormValues = z.infer<ReturnType<typeof buildSchema>>

export function NetworkEditForm({
  initialValues,
  onSubmit,
}: {
  initialValues?: NetworkInput
  onSubmit: SubmitHandler<NetworkFormValues>
}) {
  const { t } = useTranslation('feature')
  const schema = useMemo(() => buildSchema(t('network.form.required')), [t])
  const { control, handleSubmit, formState } = useForm<NetworkFormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues ?? { name: '', apn: '', user: '', pass: '' },
  })
  const editing = !!initialValues

  const fields: { name: keyof NetworkFormValues; label: string }[] = [
    { name: 'name', label: 'network.form.name' },
    { name: 'apn', label: 'network.form.apn' },
    { name: 'user', label: 'network.form.user' },
    { name: 'pass', label: 'network.form.pass' },
  ]

  return (
    <form className="mx-auto flex w-full max-w-2xl flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
      <UnsavedChangesGuard when={formState.isDirty && !formState.isSubmitSuccessful} />
      {fields.map(({ name, label }) => (
        <Controller
          key={name}
          control={control}
          name={name}
          render={({ field, fieldState }) => (
            <Field data-invalid={!!fieldState.error}>
              <FieldLabel htmlFor={`network-${name}`}>
                {t(label as 'network.form.name')}
              </FieldLabel>
              <Input id={`network-${name}`} type={name === 'pass' ? 'password' : 'text'} {...field} />
              {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
            </Field>
          )}
        />
      ))}

      <Button type="submit" disabled={formState.isSubmitting}>
        {formState.isSubmitting
          ? t(editing ? 'network.form.editing' : 'network.form.submitting')
          : t(editing ? 'network.form.edit' : 'network.form.submit')}
      </Button>
    </form>
  )
}
